import { Link } from 'react-router-dom'
import { Fragment } from 'react'
import { useAuthStore } from '../stores/authStore'
import { Bars3Icon } from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import NotificationBell from './notifications/NotificationBell'
import { canAccessCertificates } from '../utils/tierPermissions'

export default function Navbar({ onMenuClick }) {
  const { user, logout } = useAuthStore()
  const hasCertificateAccess = canAccessCertificates(user?.enrolled_tier)

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-full mx-auto px-1 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-12 sm:h-14 lg:h-16">
          <div className="flex items-center min-w-0 flex-1 gap-1 sm:gap-2">
            <button
              onClick={onMenuClick}
              className="p-1.5 sm:p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-cte-blue transition-colors flex-shrink-0"
              aria-label="Toggle menu"
            >
              <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            
            <Link to="/dashboard" className="flex items-center min-w-0">
              <div className="flex-shrink-0">
                <img 
                  src="/branding/cte-course-platform-icon.svg" 
                  alt="CTE Course Platform" 
                  className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8"
                />
              </div>
              <span className="ml-1.5 sm:ml-2 lg:ml-3 text-xs sm:text-sm lg:text-base font-semibold text-cte-black hidden sm:inline truncate max-w-[120px] sm:max-w-none">
                Course Platform
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-0.5 sm:gap-1 lg:gap-2 flex-shrink-0">
            {/* Notification Bell */}
            <NotificationBell />

            {/* User Menu */}
            <Menu as="div" className="relative">
              {({ open }) => (
                <>
                  <div>
                    <Menu.Button className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cte-blue p-0.5 sm:p-1 transition-all hover:bg-gray-50">
                      <img
                        className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 rounded-full flex-shrink-0"
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=4F46E5&color=fff`}
                        alt={user?.name}
                      />
                      <span className="ml-2 text-sm text-gray-700 hidden lg:inline max-w-[120px] xl:max-w-[150px] truncate">{user?.name}</span>
                    </Menu.Button>
                  </div>
                  
                  <Transition
                    show={open}
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/profile"
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } block px-4 py-2 text-sm text-gray-700`}
                            >
                              Profile
                            </Link>
                          )}
                        </Menu.Item>
                        
                        {/* Only show Certificate link if user has access (Complete tier+) */}
                        {hasCertificateAccess && (
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/certificate"
                                className={`${
                                  active ? 'bg-gray-100' : ''
                                } block px-4 py-2 text-sm text-gray-700`}
                              >
                                My Certificate
                              </Link>
                            )}
                          </Menu.Item>
                        )}
                        
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={logout}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                            >
                              Sign out
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>
          </div>
        </div>
      </div>
    </nav>
  )
}
